// libraries
import { useState } from 'react';
import styled from 'styled-components';
import moment, { Moment } from 'moment';
import { Button, DatePicker, notification, Switch, TimePicker } from 'antd';

import { txt } from '../../locales/i18n';

export type MomentGeneratorProps = { setMomentSets: (momentSets: [Moment, Moment][]) => void };

export function MomentGenerator({ setMomentSets }: MomentGeneratorProps): JSX.Element {
	const [startTime, setStartTime] = useState<Moment | null>(moment('08:00', 'HH:mm'));
	const [endTime, setEndTime] = useState<Moment | null>(moment('09:00', 'HH:mm'));
	const [dates, setDates] = useState<[Moment | null, Moment | null] | null>([null, null]);
	const [days, setDays] = useState<number[]>([]);

	function handleCreateDates(): any {
		const [startDate, endDate]: [Moment | null, Moment | null] = dates ?? [null, null];
		const dateSets: [Moment, Moment][] = [];

		if (startTime === null || endTime === null) {
			notification.error({ message: 'Missing time inputs' });
			return;
		}

		if (startDate === null || endDate === null) {
			notification.error({ message: 'Missing date inputs' });
			return;
		}

		if (days.length === 0) {
			notification.error({ message: 'Missing day input' });
			return;
		}

		startDate.set({ seconds: 0, millisecond: 0 });
		endDate.set({ seconds: 0, millisecond: 0 });

		for (const currentDate: Moment = startDate.clone(); currentDate <= endDate; currentDate.add(1, 'days')) {
			const dayIndex: number = currentDate.get('day');

			if (days.includes(dayIndex) === false) {
				continue;
			}

			const start: Moment = currentDate.clone();
			start.set('hour', startTime?.get('hour') ?? 0);
			start.set('minutes', startTime?.get('minutes') ?? 0);

			const end: Moment = currentDate.clone();
			end.set('hour', endTime?.get('hour') ?? 0);
			end.set('minutes', endTime?.get('minutes') ?? 0);

			if (startTime?.get('hour') > endTime?.get('hour')) {
				end.add(1, 'days');
			}

			const dateSet: [Moment, Moment] = [start, end];
			dateSets.push(dateSet);
		}

		setMomentSets(dateSets);
	}

	function toggleDay(day: number): void {
		let daysCurrent: number[] = [...days];
		const exits: boolean = daysCurrent.includes(day);

		if (exits === true) {
			daysCurrent = daysCurrent.filter((value: number) => value !== day);
		} else {
			daysCurrent.push(day);
		}

		setDays(daysCurrent);
	}

	return (
		<StyledDiv>
			<div className="days-container">
				<Switch
					checkedChildren={txt('days.mon')}
					unCheckedChildren={txt('days.mon')}
					onChange={() => toggleDay(1)}
					checked={days.includes(1)}
				/>
				<Switch
					checkedChildren={txt('days.tue')}
					unCheckedChildren={txt('days.tue')}
					onChange={() => toggleDay(2)}
					checked={days.includes(2)}
				/>
				<Switch
					checkedChildren={txt('days.wed')}
					unCheckedChildren={txt('days.wed')}
					onChange={() => toggleDay(3)}
					checked={days.includes(3)}
				/>
				<Switch
					checkedChildren={txt('days.thu')}
					unCheckedChildren={txt('days.thu')}
					onChange={() => toggleDay(4)}
					checked={days.includes(4)}
				/>
				<Switch
					checkedChildren={txt('days.fri')}
					unCheckedChildren={txt('days.fri')}
					onChange={() => toggleDay(5)}
					checked={days.includes(5)}
				/>
				<Switch
					checkedChildren={txt('days.sat')}
					unCheckedChildren={txt('days.sat')}
					onChange={() => toggleDay(6)}
					checked={days.includes(6)}
				/>
				<Switch
					checkedChildren={txt('days.sun')}
					unCheckedChildren={txt('days.sun')}
					onChange={() => toggleDay(0)}
					checked={days.includes(0)}
				/>
			</div>

			<div className="dates-container">
				<TimePicker
					format="HH:mm"
					minuteStep={5}
					showSecond={false}
					allowClear={false}
					value={startTime}
					onChange={(value) => setStartTime(value)}
				/>

				<TimePicker
					format="HH:mm"
					minuteStep={5}
					showSecond={false}
					allowClear={false}
					value={endTime}
					onChange={(value) => setEndTime(value)}
				/>

				<DatePicker.RangePicker
					format="ddd DD/MM-YY"
					allowEmpty={[false, false]}
					allowClear={false}
					value={dates}
					onChange={(value) => setDates(value)}
				/>
			</div>
			<Button onClick={handleCreateDates}>{txt('dates.generate dates')}</Button>
		</StyledDiv>
	);
}

const StyledDiv = styled.div`
	display: flex;
	gap: 1rem;
	flex-direction: column;
	align-items: flex-start;

	.days-container,
	.dates-container {
		display: flex;
		gap: 0.6rem;
		flex-wrap: wrap;
	}

	button.ant-switch {
		zoom: 1.4;

		span {
			font-size: 0.65em;
		}
	}
`;
